import { useEffect, useState } from 'react';
import { useAppSelector } from '../store';
import { selectStationCode } from '../store/slices/shiftPlanSlice';
import { isFeatureEnabled } from '../services/featureEnabledService';
import { isLocalEnvironment } from '../utils/environmentDetection';

/**
 * Resolves a site-gated feature flag for the currently selected station.
 *
 * Returns `null` while unresolved, then the boolean result. Local dev short-circuits to `true`
 * (getUserName() can't resolve without a Boson parent frame, so we don't call the facade locally).
 * The selected station is passed to the facade so it can gate by site
 * (FeatureGating.&lt;feature&gt;.AllowedSites) in addition to user; the check re-resolves when the
 * station changes (it loads async). Fails closed (`false`) on error.
 *
 * Shared by the local-state feature gates (In-Shift V2, EOS V2); the Redux-backed Published gate
 * uses {@link useResolvePublishedShiftPlanV2} instead.
 */
export const useSiteGatedFeature = (featureName: string): boolean | null => {
  const stationCode = useAppSelector(selectStationCode);
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (isLocalEnvironment()) {
      setEnabled(true);
      return undefined;
    }
    // Cancel on unmount / dep change so a superseded (e.g. previous-station) response can't
    // win out of order or write state after unmount.
    let cancelled = false;
    isFeatureEnabled(featureName, stationCode ?? '')
      .then((value) => {
        if (!cancelled) {
          setEnabled(value);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [featureName, stationCode]);

  return enabled;
};
